import React from 'react';
import { Phone } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useActiveUsers } from '../../../hooks/useActiveUsers';
import { BRANCH_SMS_NUMBERS, REALM_DEFAULT_FROM_NUMBER, type BranchSmsNumber } from '../../../config/dialpadBranches';

/** Format E.164 phone to readable: +18313751463 → (831) 375-1463 */
function formatPhone(e164: string): string {
  const digits = e164.replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length === 10) {
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }
  return e164;
}

export interface FromSelectorProps {
  /** Currently selected "from" phone number (E.164) */
  value: string;
  /** Called when user selects a different from-number */
  onChange: (phoneNumber: string) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Compact styling */
  compact?: boolean;
  /** When set, filter department lines to only the realm's default number */
  realmId?: string;
}

interface FromOption {
  readonly phoneNumber: string;
  readonly label: string;
  readonly type: 'department' | 'personal';
}

/**
 * Build the list of "send from" options:
 * 1. Department lines from BRANCH_SMS_NUMBERS
 * 2. User's personal Dialpad number (if available in profile)
 */
function buildFromOptions(
  personalPhone?: string,
  userName?: string,
  realmId?: string,
): readonly FromOption[] {
  const realmDefault = realmId ? REALM_DEFAULT_FROM_NUMBER[realmId] : undefined;
  const filteredBranches = realmDefault
    ? BRANCH_SMS_NUMBERS.filter((b) => b.phoneNumber === realmDefault)
    : BRANCH_SMS_NUMBERS;
  const departmentOptions: FromOption[] = filteredBranches.map(
    (branch: BranchSmsNumber) => ({
      phoneNumber: branch.phoneNumber,
      label: `${branch.label} ${formatPhone(branch.phoneNumber)}`,
      type: 'department' as const,
    })
  );

  if (personalPhone) {
    const name = userName || 'My Line';
    return [
      ...departmentOptions,
      {
        phoneNumber: personalPhone,
        label: `${name} ${formatPhone(personalPhone)}`,
        type: 'personal' as const,
      },
    ];
  }

  return departmentOptions;
}

/**
 * FromSelector - Dropdown to choose which phone number to send SMS from.
 *
 * Shows department lines (Tracy, Seaside, Reno) and the current user's
 * personal Dialpad number if available in their profile.
 */
export function FromSelector({
  value,
  onChange,
  disabled = false,
  compact = false,
  realmId,
}: FromSelectorProps) {
  const { user } = useAuth();
  const { data: activeUsers = [] } = useActiveUsers();
  // Read dialpadNumber from activeUsers (fresh via React Query) instead of
  // stale auth cache which only loads once at login
  const freshUser = activeUsers.find(u => u.userId === user?.userId);
  const personalPhone = freshUser?.profile?.dialpadNumber ?? user?.profile?.dialpadNumber;
  const userName = user?.displayName || undefined;
  const options = buildFromOptions(personalPhone, userName, realmId);

  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor="sms-from-selector"
        className={`flex items-center gap-1 text-slate-600 whitespace-nowrap ${
          compact ? 'text-xs' : 'text-sm'
        }`}
      >
        <Phone className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
        From:
      </label>
      <select
        id="sms-from-selector"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`
          flex-1 border border-slate-200 rounded bg-white
          transition-colors duration-200
          focus:border-navy-500 focus:ring-1 focus:ring-navy-200
          disabled:bg-slate-100 disabled:cursor-not-allowed
          ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm min-h-[44px]'}
        `}
      >
        {!value && (
          <option value="" disabled>
            Select a line...
          </option>
        )}
        {options.length > 0 && (
          <optgroup label="Department Lines">
            {options
              .filter((o) => o.type === 'department')
              .map((option) => (
                <option key={option.phoneNumber} value={option.phoneNumber}>
                  {option.label}
                </option>
              ))}
          </optgroup>
        )}
        {options.some((o) => o.type === 'personal') && (
          <optgroup label="Personal">
            {options
              .filter((o) => o.type === 'personal')
              .map((option) => (
                <option key={option.phoneNumber} value={option.phoneNumber}>
                  {option.label}
                </option>
              ))}
          </optgroup>
        )}
      </select>
    </div>
  );
}
